import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlusIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon, PencilIcon, PhoneIcon, TriangleAlertIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { PersonalDataModal } from '../../components/shared/PersonalDataModal';
import { attendanceSummary } from '../../utils/attendance';
import { missingFields } from '../../utils/personal';
import { documentTypeLabel, eventTitle, formatDate, functionLabel, maskiraj, memberTypeClass, memberTypeLabel, personalFieldLabel, voiceClass, voiceLabel } from '../../utils/labels';
export function MemberProfile() {
  const {
    currentMember,
    settings,
    fees,
    upcoming,
    isExpected
  } = useApp();
  const me = currentMember!;
  const povzetek = attendanceSummary(me, settings);
  const jeGost = me.type === 'gostujoci';
  const clanarina = fees.find(f => f.memberId === me.id && f.leto === settings.letoClanarine);
  const [odprto, setOdprto] = useState(false);
  const [urejanje, setUrejanje] = useState(false);

  /** Potovalni dogodki, za katere član še ni dopolnil zahtevanih podatkov. */
  const manjkaZaPot = useMemo(() => {
    return upcoming.filter(e => e.potovanje?.aktivno && isExpected(e, me.id)).map(e => ({
      e,
      manjka: missingFields(me, e.potovanje!.obveznaPolja)
    })).filter(x => x.manjka.length > 0);
  }, [upcoming, isExpected, me]);
  const zasebno = [{
    label: personalFieldLabel.naslov,
    value: me.naslov ? `${me.naslov}, ${me.postnaStevilka ?? ''} ${me.posta ?? ''}`.trim() : undefined
  }, {
    label: personalFieldLabel.datumRojstva,
    value: me.datumRojstva ? formatDate(me.datumRojstva) : undefined
  }, {
    label: personalFieldLabel.dokumentTip,
    value: me.dokumentTip ? documentTypeLabel[me.dokumentTip] : undefined
  }, {
    label: personalFieldLabel.dokumentStevilka,
    value: me.dokumentStevilka,
    maskirano: true
  }, {
    label: personalFieldLabel.prehrana,
    value: me.prehrana
  }];
  return <div className="mx-auto max-w-3xl space-y-4" data-id="element-1700" data-mp="1wproszyskpmc">
      <h1 className="text-2xl font-semibold text-ink" data-id="element-1701" data-mp="1wo6qr0d9qojq">Moj profil</h1>

      <Card className="p-5" data-id="element-1702" data-mp="2agd7ankc2xjp">
        <div className="flex items-center gap-4" data-id="element-1703" data-mp="2ajrb808nbet9">
          <Avatar member={me} size="lg" data-id="element-1704" data-mp="26guktncv6eer" />
          <div data-id="element-1705" data-mp="26ftchqn8g60q">
            <h2 className="text-lg font-semibold text-ink" data-id="element-1706" data-mp="26iffdcz6hxcm">
              {me.ime} {me.priimek}
            </h2>
            <p className="text-[13px] text-ink-muted" data-id="element-1707" data-mp="2fgre62a781g5">Član/ica od {formatDate(me.clanOd)}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-1708" data-mp="q11hd5ypcknq">
          <div data-id="element-1709" data-mp="q3o081r1mml9">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1710" data-mp="l2qa92zsko97">Tip članstva</p>
            <div className="mt-1.5" data-id="element-1711" data-mp="2wqd5dfz4xf7n">
              <Badge className={memberTypeClass[me.type]} data-id="element-1712" data-mp="2wrej5s7jcft2">{memberTypeLabel[me.type]}</Badge>
            </div>
          </div>
          <div data-id="element-1713" data-mp="31r9afwv837en">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1714" data-mp="2n0a5qwxid30c">Glasovna skupina</p>
            <div className="mt-1.5" data-id="element-1715" data-mp="2rz3kesoj7023">
              <Badge className={voiceClass[me.voice]} data-id="element-1716" data-mp="2ry286kvs47is">{voiceLabel[me.voice]}</Badge>
            </div>
          </div>
          <div data-id="element-1717" data-mp="2dy0zij99vvsr">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1718" data-mp="2e1os9g6w8e6f">Funkcije v zboru</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1719" data-mp="241fq7lq7zyj4">
              {me.functions.length === 0 ? <span className="text-[13px] text-ink-muted" data-id="element-1720" data-mp="290itb0mn3uog">Brez funkcije</span> : me.functions.map(f => <Badge key={f} data-id="element-1721" data-mp="29t8ybw4rhzu">{functionLabel[f]}</Badge>)}
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-2 border-t border-line pt-4 text-sm text-ink-muted sm:grid-cols-2" data-id="element-1722" data-mp="77us0wtk3hbb">
          <p className="flex items-center gap-2" data-id="element-1723" data-mp="23br10jdfhusm">
            <MailIcon className="h-4 w-4" data-id="element-1724" data-mp="1zayrrq4k3pwc" />
            {me.email}
          </p>
          <p className="flex items-center gap-2" data-id="element-1725" data-mp="1z8urdi5v990t">
            <PhoneIcon className="h-4 w-4" data-id="element-1726" data-mp="1uajc2rwsy6b7" />
            {me.telefon}
          </p>
        </div>
      </Card>

      {manjkaZaPot.length > 0 && <Card className="border-amber-200 bg-amber-50/60 p-4" data-id="element-1727" data-mp="1pevo2fu23j7a">
          <div className="flex items-start gap-3" data-id="element-1728" data-mp="1kezl8jx3hxtb">
            <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-1729" data-mp="1fmydnbavgv9m" />
            <div className="flex-1" data-id="element-1730" data-mp="1fkvg867e3zuv">
              <p className="text-sm font-medium text-ink" data-id="element-1731" data-mp="1bkbjh3qqay99">
                Za {eventTitle(manjkaZaPot[0].e)} morate dopolniti osebne podatke.
              </p>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1732" data-mp="16k71f9a69vgx">
                Manjka:{' '}
                {manjkaZaPot[0].manjka.map(p => personalFieldLabel[p].toLowerCase()).join(', ')}.
              </p>
              <Button size="sm" className="mt-3" onClick={() => setUrejanje(true)} data-id="element-1733" data-mp="mk5o8v2kk29p">
                Dopolni osebne podatke
              </Button>
            </div>
          </div>
        </Card>}

      <Card className="p-5" data-id="element-1734" data-mp="wicq06oaedr4">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1735" data-mp="wj4mdjppkfig">
          <div data-id="element-1736" data-mp="11gwhemlnghqv">
            <div className="flex items-center gap-2" data-id="element-1737" data-mp="11hfy5zzkd03b">
              <h2 className="text-base font-semibold text-ink" data-id="element-1738" data-mp="11etre2khyom5">Osebni podatki</h2>
              <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-1739" data-mp="3v8semrix1pd">
                <LockIcon className="h-3 w-3" data-id="element-1740" data-mp="8taap5h4kwzs" />
                Zasebno
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1741" data-mp="dq0rjv0r7wm9">
              Podatke uporabljamo za organizacijo gostovanj. Drugi člani jih ne vidijo.
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1742" data-mp="3h796a606qgcr">
            <Button variant="secondary" size="sm" onClick={() => setOdprto(v => !v)} data-id="element-1743" data-mp="3h67uu0f9brui">
              {odprto ? <EyeOffIcon className="h-4 w-4" data-id="element-1744" data-mp="3m5aoyptp8dnp" /> : <EyeIcon className="h-4 w-4" data-id="element-1745" data-mp="3e6fpbtgqpbvd" />}
              {odprto ? 'Skrij' : 'Pokaži'}
            </Button>
            <Button size="sm" onClick={() => setUrejanje(true)} data-id="element-1746" data-mp="398e9riiivuon">
              <PencilIcon className="h-4 w-4" data-id="element-1747" data-mp="395s09dc2n53s" />
              Uredi podatke
            </Button>
          </div>
        </div>

        {odprto ? <dl className="mt-4 divide-y divide-line border-t border-line" data-id="element-1748" data-mp="3iyh1zghtl4lx">
            {zasebno.map(v => <div key={v.label} className="flex flex-wrap items-center gap-2 py-2.5" data-id="element-1749" data-mp="2v8mpj01exi27">
                <dt className="w-56 shrink-0 text-[13px] text-ink-muted" data-id="element-1750" data-mp="2q9tkzx43t1e6">{v.label}</dt>
                <dd className="text-sm text-ink" data-id="element-1751" data-mp="356ausjwna7ym">
                  {v.value ? v.maskirano ? <span className="font-mono" data-id="element-1752" data-mp="30651z0en2fey">{maskiraj(v.value)}</span> : v.value : <span className="text-amber-700" data-id="element-1753" data-mp="fhiailc1udxf">Ni vpisano</span>}
                </dd>
              </div>)}
          </dl> : <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-[13px] text-ink-muted" data-id="element-1754" data-mp="rzbaisa3gcl6">
            Naslov, datum rojstva, podatki o dokumentu in posebnosti pri prehrani so skriti. Kliknite
            „Pokaži“ za pregled.
          </p>}
      </Card>

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-1755" data-mp="i1nbhq5ggix7">
        <Card className="p-5" data-id="element-1756" data-mp="15qzh6g1kq8ur">
          <div className="flex items-start justify-between gap-3" data-id="element-1757" data-mp="15s0vquhstdh8">
            <h2 className="text-base font-semibold text-ink" data-id="element-1758" data-mp="1aqc5z6c09pzz">Prisotnost</h2>
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} data-id="element-1759" data-mp="wye6zoc0dahj" />}
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1760" data-mp="10zzmzijua4vy">
            {povzetek.prisoten} od {povzetek.pricakovan} dogodkov v sezoni 2026/27.
          </p>
          <Link to="/clan/prisotnost" className="mt-2 inline-block text-[13px] font-medium text-green-600 hover:underline" data-id="element-1761" data-mp="1oosd2yds5iin">
            Podroben pregled
          </Link>
        </Card>

        <Card className="p-5" data-id="element-1762" data-mp="14qz2i54mfe2d">
          <div className="flex items-start justify-between gap-3" data-id="element-1763" data-mp="zyxpggzmpjba">
            <h2 className="text-base font-semibold text-ink" data-id="element-1764" data-mp="zxmpks9b0vpg">Članarina {settings.letoClanarine}</h2>
            <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1765" data-mp="v04gn1vaeghg">
              Poravnano
            </Badge>
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1766" data-mp="1nos3v2zljfxn">
            {clanarina?.znesek ?? settings.znesekClanarine} € · plačano{' '}
            {clanarina?.datumPlacila ? formatDate(clanarina.datumPlacila) : '—'}
          </p>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1767" data-mp="1dp170unkcoue">Za leto 2027 boste položnico prejeli januarja.</p>
        </Card>
      </div>

      <Card className="p-5" data-id="element-1768" data-mp="18tc8muna5dzo">
        <h2 className="text-base font-semibold text-ink" data-id="element-1769" data-mp="2axdfpkw12pv">Odsotnosti</h2>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1770" data-mp="28t2woshh5gm">
          Sporočite daljšo odsotnost vnaprej, da vodstvo lažje načrtuje vaje.
        </p>
        <Link to="/clan/odsotnost" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1771" data-mp="2907kfsta2ba0">
          <CalendarPlusIcon className="h-4 w-4" data-id="element-1772" data-mp="2ivsqc21gvvp1" />
          Prijavi daljšo odsotnost
        </Link>
      </Card>

      <PersonalDataModal member={me} open={urejanje} onClose={() => setUrejanje(false)} data-id="element-1773" data-mp="2snf86h8b8t0r" />
    </div>;
}